import {motion, Variants} from 'framer-motion';
import {showNotification} from 'platform/components';
import {Box} from 'platform/foundation';

import {useCallback, useMemo, useState} from 'react';

import {useActionCallbackProxy} from '../hooks/useActionCallbackProxy';
import {useBulkActions} from '../hooks/useBulkActions';
import {useGridApiEventListener} from '../hooks/useGridApiEventListener';
import {AgGridEvent, GridApi} from '../types/AgGridTypes';
import {FeGridSettings, TransformedDefinitionApiResponse} from '../types/Api';
import {DataGridProps} from '../types/DataGridProps';
import {BulkActionsPanelRenderer} from './BulkActionsPanelRenderer';

export interface BulkActionsPanelWrapperProps {
  gridProps: DataGridProps;
  api: GridApi;
  settings: FeGridSettings;
  definition: TransformedDefinitionApiResponse;
  disableAllActions?: boolean;
}

export function BulkActionsPanelWrapper({
  gridProps,
  api,
  definition,
  disableAllActions,
}: BulkActionsPanelWrapperProps) {
  const {actionCallback, gridCode} = gridProps;
  const [selectedNodes, setSelectedNodes] = useState(api.getSelectedNodes());

  // Filter out nodes that don't have data (likely deleted/removed rows)
  const validSelectedNodes = selectedNodes.filter((node) => node.data);

  if (selectedNodes.length !== validSelectedNodes.length) {
    showNotification.error(`You have selected a row that has been deleted or removed`);
  }

  const actionsOfSelectedNodes = validSelectedNodes.map((node) => node.data.actions);
  const validBulkActions = useBulkActions(
    // @ts-ignore
    gridProps.actionsDefinition || {},
    actionsOfSelectedNodes
  );

  const deselectAll = useCallback(() => {
    if (api.isDestroyed()) {
      return;
    }
    api.deselectAll();
    api.refreshCells({
      suppressFlash: true,
      columns: ['eag-col-select'],
    });
    try {
      api.refreshHeader();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('refreshHeader failed - column groups may not be initialized yet:', error);
      }
    }
  }, [api]);

  const selectedRows = useMemo(() => selectedNodes.map((node) => node.data), [selectedNodes]);
  const selectedNodeIds = useMemo(
    () => selectedNodes.map((node) => node.id as string),
    [selectedNodes]
  );

  const [actionCallbackProxy, isLoading] = useActionCallbackProxy(
    actionCallback,
    api,
    selectedNodeIds,
    // @ts-ignore
    selectedRows,
    gridCode
  );

  const selectableRows = api.getDisplayedRowCount();

  const {addListeners} = useGridApiEventListener(
    api,
    ['rowSelected', 'storeRefreshed'],
    ({api}: AgGridEvent) => {
      setSelectedNodes(api.getSelectedNodes());
    }
  );
  addListeners();

  if (
    Object.keys(validBulkActions).length === 0 &&
    definition.behavior.rowSelectMode === 'SINGLE'
  ) {
    return null;
  }

  return (
    <MotionBox
      variants={barMotionVariants}
      animate={selectedNodeIds.length > 0 ? 'open' : 'close'}
      height={0}
      css={{
        height: 0,
        marginBottom: '-2px',
        borderBottom: '1px solid',
        borderBottomColor: '#E2E8F0',
        boxSizing: 'content-box',
        overflow: 'hidden',

        '&>div': {
          width: '100%',
          height: '56px',
          bg: '#F7FAFC',
          display: 'flex',
          paddingLeft: '16px',
          paddingRight: '16px',
        },
      }}
    >
      <Box>
        <BulkActionsPanelRenderer
          deselectAll={deselectAll}
          selectableRows={selectableRows}
          isLoading={isLoading}
          disableAllActions={disableAllActions}
          selectedRows={selectedNodeIds.length}
          bulkActions={validBulkActions}
          actionCallback={actionCallbackProxy}
          rowSelection={definition.behavior.rowSelectMode}
        />
      </Box>
    </MotionBox>
  );
}

const MotionBox = motion.create(Box);

const barMotionVariants: Variants = {
  open: {
    height: '65px',
    marginBottom: 0,
    transitionEnd: {
      marginBottom: 0,
      height: '65px',
    },
    transition: {
      duration: 0.65,
      easings: [0.4, 0.14, 0.3, 1],
    },
  },
  close: {
    height: '0',
    marginBottom: '-1px',
    transitionEnd: {
      marginBottom: '-1px',
      height: '0',
    },
    transition: {
      duration: 0.65,
      easings: [0.4, 0.14, 0.3, 1],
    },
  },
};
